import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface LigneCommandeDetails {
  id: number;
  numero_commande: string;
  date_expedition?: string;
  type_capteur?: string;
  cartouche_a?: number;
  nom_cartouche_a?: string;
  cartouche_b?: number;
  nom_cartouche_b?: string;
}



export interface Commande {
  id: number;
  numero_commande: string;
  statut: string;
  client_id: number;
  nombre_capteurs?: number | null;
}

export interface CommandeListItem extends Commande {
  client_identifiant?: string | null;
}

export interface CommandeListResponse {
  items: CommandeListItem[];
  count: number;
}

export interface CommandePayload {
  numero_commande: string;
  statut: string;
  adresse_facturation_id: number;
  adresses_livraison_ids: number[];
  client_id: number;
  nombre_capteurs?: number | null;
}

export interface LigneCommande {
  id: number;
  adresse_livraison_id?: number;
  commande_id: number;
  type_capteur?: string;
  cartouche_a?: number | null;
  cartouche_b?: number | null;
  date_expedition?: string;
  statut?: number | null;
  statut_nom?: string | null;
}

export interface LigneCommandePayload {
  adresse_livraison_id?: number;
  commande_id: number;
  type_capteur?: string;
  cartouche_a?: number | null;
  cartouche_b?: number | null;
  date_expedition?: string;
}

export interface LignesPreparationItem {
  id: number;
  commande_id: number;
  numero_commande?: string | null;
  adresse_livraison_id?: number | null;
  type_capteur?: string | null;
  cartouche_a?: number | null;
  cartouche_b?: number | null;
  nom_cartouche_a?: string | null;
  nom_cartouche_b?: string | null;
  date_expedition?: string | null;
  statut?: number | null;
  statut_nom?: string | null;
}

export interface LignesPreparationResponse {
  items: LignesPreparationItem[];
  count: number;
}



  

@Injectable({ providedIn: 'root' })
export class CommandesService {
  private readonly baseUrl = `${environment.apiBaseUrl}/api/commandes`;

  constructor(private http: HttpClient) {}

  list(params?: { skip?: number; limit?: number; search?: string }): Observable<Commande[]> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.skip !== undefined) httpParams = httpParams.set('skip', params.skip.toString());
      if (params.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString());
      if (params.search) httpParams = httpParams.set('search', params.search);
    }
    return this.http.get<Commande[] | CommandeListResponse>(this.baseUrl, { params: httpParams }).pipe(
      map((response) => {
        if (Array.isArray(response)) {
          return response;
        }
        return Array.isArray(response?.items) ? response.items : [];
      })
    );
  }

  listWithCount(params?: { skip?: number; limit?: number; search?: string }): Observable<CommandeListResponse> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.skip !== undefined) httpParams = httpParams.set('skip', params.skip.toString());
      if (params.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString());
      if (params.search) httpParams = httpParams.set('search', params.search);
    }
    return this.http.get<CommandeListResponse>(this.baseUrl, { params: httpParams });
  }

    /**
   * Recherche enrichie pour autocomplete (numéro, date, type, noms cartouches)
   */
  autocompleteLignesDetails(search: string): Observable<LigneCommandeDetails[]> {
    const params = new HttpParams().set('search', search);
    return this.http.get<LigneCommandeDetails[]>(`${this.baseUrl}/lignes/details`, { params });
  }
  /**
   * Recherche les lignes de commande par numéro (pour lier un capteur à une ligne de commande).
   * Retourne un tableau d'objets { id, numero_commande }.
   */
  autocompleteNumeroCommande(search: string): Observable<{ id: number; numero_commande: string }[]> {
    const params = new HttpParams().set('search', search);
    return this.http.get<any[]>(`${this.baseUrl}/lignes`, { params }).pipe(
      map(lignes => lignes
        .filter(l => l.id && l.numero_commande)
        .map(l => ({ id: l.id, numero_commande: l.numero_commande }))
      )
    );
  }

  
  get(id: number): Observable<Commande> {
    return this.http.get<Commande>(`${this.baseUrl}/${id}`);
  }

  create(payload: CommandePayload): Observable<Commande> {
    return this.http.post<Commande>(this.baseUrl, payload);
  }

  update(id: number, payload: Partial<CommandePayload>): Observable<Commande> {
    return this.http.put<Commande>(`${this.baseUrl}/${id}`, payload);
  }

  delete(id: number): Observable<{ deleted: boolean; id: number }> {
    return this.http.delete<{ deleted: boolean; id: number }>(`${this.baseUrl}/${id}`);
  }

  listLignes(commandeId: number): Observable<LigneCommande[]> {
    return this.http.get<LigneCommande[]>(`${this.baseUrl}/${commandeId}/lignes`);
  }

  createLigne(commandeId: number, payload: LigneCommandePayload): Observable<LigneCommande> {
    return this.http.post<LigneCommande>(`${this.baseUrl}/${commandeId}/lignes`, payload);
  }

  updateLigne(ligneId: number, payload: Partial<LigneCommandePayload>): Observable<LigneCommande> {
    return this.http.put<LigneCommande>(`${this.baseUrl}/lignes/${ligneId}`, payload);
  }

  transfererLigne(ligneId: number): Observable<LigneCommande> {
    return this.http.post<LigneCommande>(`${this.baseUrl}/lignes/${ligneId}/transferer`, {});
  }

  deleteLigne(ligneId: number): Observable<{ deleted: boolean; id: number }> {
    return this.http.delete<{ deleted: boolean; id: number }>(`${this.baseUrl}/lignes/${ligneId}`);
  }

  /**
   * Récupère une ligne de commande enrichie par son id (pour préremplissage autocomplete)
   */
  getLigneCommandeDetailsById(id: number): Observable<LigneCommandeDetails> {
    return this.http.get<LigneCommandeDetails>(`${this.baseUrl}/lignes/details/${id}`);
  }

  listLignesEnCoursDePreparation(): Observable<LignesPreparationResponse> {
    return this.http.get<LignesPreparationResponse>(`${this.baseUrl}/lignes/en-cours-de-livraison`);
  }
}
